import React, { useState } from 'react';

import { track } from '../../services/analytics';
import { BaseGym, Discount } from '../../types/Gym';
import { User } from '../../types/authentication';
import { trackAddToCart } from '../../utils/tracking';

import { FormattedPrice } from '../FormattedPrice/FormattedPrice';
import { ProductDiscountMessage } from './ProductDiscountMessage';
import { Modal } from '../Modal/Modal';
import { DirectModal } from './DirectModal';
import { Dash, Tick } from '../../components/Icons';
import { Button, SrOnly } from '@husslehq/ui-library';

import styles from './Matrix.module.scss';

export type MatrixProps = {
  gym: BaseGym;
  monthlyProductId: string;
  monthlyPrice: number;
  monthlyDiscount?: Discount;
  directProductId: string;
  directPrice: number;
  directOriginalPrice?: number;
  directRecommended?: boolean;
  user?: User;
};

export const Matrix: React.FC<MatrixProps> = ({
  gym,
  monthlyProductId,
  monthlyPrice,
  monthlyDiscount,
  directProductId,
  directPrice,
  directOriginalPrice,
  directRecommended,
  user,
}) => {
  const [showModal, setShowModal] = useState(false);

  const hasMonthlyWithThisGym = user && user.hasMonthly && user.monthlyGymId === gym.id;
  const hasMonthlyWithAnotherGym = user && user.hasMonthly && user.monthlyGymId !== gym.id;

  const makeMainGym = () => {
    document.location.href = `/account/change-mplus-gym/${gym.id}`;
  };

  const fullProductName = (type: string) => {
    switch (type) {
      case 'monthly':
        return 'Monthly+';
      case 'direct':
        return 'Gym Membership';
      case 'daypass':
        return 'Day Pass';
    }
  };

  const buyProduct = (productType: string, productId: string | undefined, gym: BaseGym) => {
    trackAddToCart(gym, productType);
    track({
      event: 'gym_spa',
      category: 'Fake Door',
      action: `${fullProductName(productType)}`,
      label: `${gym.name}`,
    });
    track({
      event: 'gym_spa',
      category: 'Fake Door',
      action: `${fullProductName(productType)}`,
      label: `${fullProductName(productType)}`,
    });
    track({
      event: 'gym_spa',
      category: 'Add to cart - Gym details',
      action: `${fullProductName(productType)}`,
      label: `${gym.name}`,
    });
    const clearBasket = ['direct', 'monthly'].includes(productType) ? 1 : 0;
    document.location.href = `/checkout/small-basket-item-add?clear=${clearBasket}&productId=${productId}${
      productType === 'monthly' ? `&productGymId=${gym.id}` : ''
    }`;
  };

  const openDirectModal = () => {
    track({
      event: 'gym_spa',
      category: 'Gym Details',
      action: 'pass card',
      label: 'how does it work',
    });
    setShowModal(true);
  };

  const closeDirectModal = () => {
    setShowModal(false);
  };

  const included = () => {
    return (
      <>
        <SrOnly>Included</SrOnly>
        <span title="Included" className={styles.matrix__svg}>
          <Tick />
        </span>
      </>
    );
  };

  const notIncluded = () => {
    return (
      <>
        <SrOnly>Not included</SrOnly>
        <span title="Not included" className={`${styles.matrix__svg} ${styles['matrix__svg--excluded']}`}>
          <Dash />
        </span>
      </>
    );
  };

  const monthlyBuyButton = () => {
    return (
      <>
        {hasMonthlyWithThisGym && <div className={styles['matrix__primary-gym']}>This is your main gym</div>}
        {hasMonthlyWithAnotherGym && (
          <Button
            onClick={makeMainGym}
            disabled={gym.is_temporarily_closed}
            level="primary"
            fullWidth
            className={`${styles['matrix__buy-button']} ${styles['matrix__make-main-button']}`}
          >
            Make this my main gym
          </Button>
        )}
        {!hasMonthlyWithAnotherGym && !hasMonthlyWithThisGym && (
          <Button
            level="primary"
            disabled={gym.is_temporarily_closed}
            fullWidth
            className={styles['matrix__buy-button']}
            onClick={() => buyProduct('monthly', monthlyProductId, gym)}
          >
            {monthlyDiscount ? (
              <ProductDiscountMessage discount={monthlyDiscount} productType="monthly" />
            ) : (
              <>Select</>
            )}
          </Button>
        )}
      </>
    );
  };

  const directProductHeader = () => {
    return (
      <>
        <div className={`${styles.matrix__product} ${styles['matrix__product--direct']}`}>
          {directRecommended ? (
            <p className={styles.matrix__label}>Recommended</p>
          ) : (
            directOriginalPrice && directOriginalPrice > directPrice && <p className={styles.matrix__label}>Offer</p>
          )}
          <p className={styles['matrix__product-name']}>Membership</p>
        </div>
        <div className={styles.matrix__brief}>
          <div>
            {directOriginalPrice && directOriginalPrice > directPrice ? (
              <FormattedPrice
                className={styles.matrix__price}
                classNamePounds={styles['matrix__price-pounds']}
                price={directPrice}
                originalPrice={directOriginalPrice}
              />
            ) : (
              <FormattedPrice
                className={styles.matrix__price}
                classNamePounds={styles['matrix__price-pounds']}
                price={directPrice}
              />
            )}
          </div>
          <p className={styles.matrix__description}>Best for single gym use.</p>
          <Button onClick={openDirectModal} level="link" className={styles['matrix__link']}>
            How does it work?
          </Button>
          <div className={styles.matrix__buy}>
            <Button
              level="primary"
              disabled={gym.is_temporarily_closed}
              fullWidth
              className={styles['matrix__buy-button']}
              onClick={() => buyProduct('direct', directProductId, gym)}
            >
              Select
            </Button>
          </div>
        </div>
      </>
    );
  };

  const monthlyProductHeader = () => {
    return (
      <>
        <div className={`${styles.matrix__product} ${styles['matrix__product--monthly']}`}>
          {monthlyDiscount && <p className={styles.matrix__label}>Offer</p>}
          <p className={styles['matrix__product-name']}>Monthly+</p>
        </div>
        <div className={styles.matrix__brief}>
          <div>
            {monthlyDiscount && monthlyDiscount.discounted_price > 0 && !monthlyDiscount.trial_days ? (
              <FormattedPrice
                className={styles.matrix__price}
                classNamePounds={styles['matrix__price-pounds']}
                price={monthlyDiscount.discounted_price}
                originalPrice={monthlyPrice}
              />
            ) : (
              <FormattedPrice
                className={styles.matrix__price}
                classNamePounds={styles['matrix__price-pounds']}
                price={monthlyPrice}
              />
            )}
          </div>
          <p className={styles.matrix__description}>Multi-gym access.</p>
          <a href={`/search/extra-gym?id=${gym.id}&pid=${monthlyProductId}`} className={styles['matrix__link']}>
            See gyms included
          </a>
          <div className={styles.matrix__buy}>{monthlyBuyButton()}</div>
        </div>
      </>
    );
  };

  return (
    <div className={styles.matrix}>
      <div className={styles.matrix__fakebg}></div>
      <table className={styles.matrix__table}>
        <thead>
          <tr>
            <th scope="col">
              <SrOnly>Product information</SrOnly>
            </th>
            <th className={styles.matrix__col} scope="col">
              {monthlyDiscount ? monthlyProductHeader() : directProductHeader()}
            </th>
            <th scope="col">{monthlyDiscount ? directProductHeader() : monthlyProductHeader()}</th>
          </tr>
        </thead>

        <tbody>
          <tr>
            <th scope="row">Unlimited visits each month</th>
            <td>{included()}</td>
            <td>{included()}</td>
          </tr>
          <tr>
            <th scope="row">Rolling monthly subscription</th>
            <td>{included()}</td>
            <td>{included()}</td>
          </tr>
          <tr>
            <th scope="row">Multi-gym access</th>
            <td>{monthlyDiscount ? included() : notIncluded()}</td>
            <td>{monthlyDiscount ? notIncluded() : included()}</td>
          </tr>
          <tr>
            <th scope="row">Hussle partner fitness apps</th>
            <td>{monthlyDiscount ? included() : notIncluded()}</td>
            <td>{monthlyDiscount ? notIncluded() : included()}</td>
          </tr>
          <tr>
            <th scope="row">All club member perks included</th>
            <td>{monthlyDiscount ? notIncluded() : included()}</td>
            <td>{monthlyDiscount ? included() : notIncluded()}</td>
          </tr>
        </tbody>
      </table>
      <Modal onRequestClose={closeDirectModal} visible={showModal} modalClassName="ReactModal__Content--noPadding">
        <DirectModal
          onCloseModal={closeDirectModal}
          onBuyProduct={buyProduct}
          productId={directProductId}
          gym={gym}
          isTemporarilyClosed={gym.is_temporarily_closed}
        />
      </Modal>
    </div>
  );
};
